import { Button, Option, SingleSelect } from "@appretio/ui-kit";
import { useEffect, useMemo, useState } from "react";
import { getFieldLabel } from "../../forecastContext";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCopyCubeYear } from "../../hooks/manageCubes/useCopyCubeYear";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { useBudgetScope } from "../../hooks/useBudgetScope";
import { useFetchCubeYears } from "../../hooks/useFetchCubeYears";
import { useFetchVersions } from "../../hooks/useFetchVersions";
import { useForecastBaseContextOptions } from "../../hooks/useForecastBaseContextOptions";
import { useScopedBudgetVersions } from "../../hooks/useScopedBudgetVersions";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import { CubeContextFilterGrid } from "./CubeContextFilterGrid";
import {
  buttonRow,
  copyRow,
  copyRowTargetField,
  errorText,
  formColumn,
  hintText
} from "./manageCubesOperations.css";

type SourceProducer =
  | "user_versions"
  | "other_user_versions"
  | "budget_versions";

const SOURCE_PRODUCER_OPTIONS: Option[] = [
  { label: "User", value: "user_versions" },
  { label: "Other user", value: "other_user_versions" },
  { label: "Budget", value: "budget_versions" }
];

/** Props for the Copy Year operation form. */
interface CopyOperationProps {
  /** The target cube ID to copy year data into. */
  cubeId: string;
}

export function CopyOperation({ cubeId }: CopyOperationProps) {
  const [sourceProducer, setSourceProducer] =
    useState<SourceProducer>("user_versions");
  const [sourceBrand, setSourceBrand] = useState<string | undefined>();
  const [sourceCubeId, setSourceCubeId] = useState<string | undefined>();
  const [yearSource, setYearSource] = useState<string | undefined>();
  const [yearTarget, setYearTarget] = useState<string | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const {
    selections: contextSelections,
    activeFilters,
    handleFieldChange: handleContextChange,
    resetSelections
  } = useContextFilters();

  // A tenant-scoped budget belongs to no brand, so the source-brand step drops
  // out of the flow entirely (see `useBudgetScope`).
  const tenantScopedBudget = useBudgetScope() === "tenant";
  const budgetSource = sourceProducer === "budget_versions";
  const needsSourceBrand = budgetSource && !tenantScopedBudget;

  const { data: versionsData } = useFetchVersions();
  const { data: budgetVersionsData, isLoading: budgetVersionsLoading } =
    useScopedBudgetVersions(sourceBrand, budgetSource);
  const { data: brandContextOptions, isLoading: brandLoading } =
    useForecastBaseContextOptions({
      enabled: needsSourceBrand
    });
  const { data: sourceYears } = useFetchCubeYears(sourceCubeId ?? cubeId);
  const { data: targetYears } = useFetchCubeYears(null);
  const { data: cubeContext, isLoading: contextLoading } = useCubeContext(
    sourceCubeId ?? cubeId
  );

  const { data: filteredContext } = useCubeContext(
    sourceCubeId ?? cubeId,
    activeFilters
  );

  const copyCubeYear = useCopyCubeYear();

  const sourceCubeOptions: Option[] = useMemo(() => {
    const list =
      sourceProducer === "user_versions"
        ? versionsData?.user_versions
        : sourceProducer === "other_user_versions"
          ? versionsData?.other_user_versions
          : budgetVersionsData?.budget_versions;
    return (list ?? []).map((v) => ({ label: v.label, value: v.cube_id }));
  }, [sourceProducer, versionsData, budgetVersionsData]);

  // Auto-select the source cube when the producer narrows the list to a
  // single version — matches the ``TargetCubeSelector`` pattern and saves
  // the user one click when there's no real choice to make.
  useEffect(() => {
    if (!sourceCubeId && sourceCubeOptions.length === 1) {
      setSourceCubeId(sourceCubeOptions[0].value);
    }
  }, [sourceCubeId, sourceCubeOptions]);

  const brandOptions: Option[] = useMemo(
    () =>
      (brandContextOptions?.brand ?? []).map((b: string) => ({
        label: b,
        value: b
      })),
    [brandContextOptions?.brand]
  );

  const sourceYearOptions: Option[] = useMemo(
    () =>
      (sourceYears?.years ?? []).map((y: string) => ({ label: y, value: y })),
    [sourceYears]
  );

  const targetYearOptions: Option[] = useMemo(
    () =>
      (targetYears?.years ?? []).map((y: string) => ({ label: y, value: y })),
    [targetYears]
  );

  const isSameCubeAsTarget =
    sourceProducer === "user_versions" &&
    (!sourceCubeId || sourceCubeId === cubeId);
  const sourceCubePicked =
    sourceProducer === "user_versions" ? true : !!sourceCubeId;

  const isValid =
    !!yearSource &&
    !!yearTarget &&
    sourceCubePicked &&
    !(isSameCubeAsTarget && yearSource === yearTarget);

  function getDisabledReason(): string | undefined {
    if (!sourceCubePicked) return "Pick a source cube";
    if (!yearSource) return "Pick a source year";
    if (!yearTarget) return "Pick a target year";
    if (isSameCubeAsTarget && yearSource === yearTarget) {
      return "Source and target year must differ when copying within the same cube";
    }
    return undefined;
  }

  function handleProducerChange(val: string) {
    const next = (val || "user_versions") as SourceProducer;
    setSourceProducer(next);
    setSourceBrand(undefined);
    setSourceCubeId(undefined);
    setYearSource(undefined);
  }

  function handleBrandChange(val: string) {
    setSourceBrand(val || undefined);
    setSourceCubeId(undefined);
    setYearSource(undefined);
  }

  function getSourceCubePlaceholder() {
    if (sourceProducer === "user_versions") return "Same as target cube";
    if (sourceProducer === "other_user_versions") return "Select a user's cube";
    if (!needsSourceBrand) return "Select a budget version";
    return sourceBrand ? "Select a budget version" : "Pick a brand first";
  }

  function handleConfirm() {
    if (!yearSource || !yearTarget) return;

    const context =
      Object.keys(contextSelections).length > 0 ? contextSelections : undefined;

    copyCubeYear.mutate(
      {
        cube_id: cubeId,
        source_cube_id: sourceCubeId !== cubeId ? sourceCubeId : null,
        year_source: yearSource,
        year_target: yearTarget,
        context: context as Record<string, string[]> | undefined
      },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          setYearSource(undefined);
          setYearTarget(undefined);
          setSourceCubeId(undefined);
          setSourceBrand(undefined);
          setSourceProducer("user_versions");
          resetSelections();
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  const producerLabel =
    SOURCE_PRODUCER_OPTIONS.find((o) => o.value === sourceProducer)?.label ??
    "";
  const versionLabel =
    sourceCubeOptions.find((o) => o.value === sourceCubeId)?.label ??
    (sourceProducer === "user_versions" ? "target cube" : "—");

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        <p className={hintText}>
          Copy a year of data from a source cube into the target cube's chosen
          year.
        </p>
        <div className={copyRow}>
          <SingleSelect
            label="Source producer"
            options={SOURCE_PRODUCER_OPTIONS}
            selectedOption={SOURCE_PRODUCER_OPTIONS.find(
              (o) => o.value === sourceProducer
            )}
            onChange={handleProducerChange}
            clearable={false}
            size="md"
          />

          {needsSourceBrand && (
            <SingleSelect
              label="Source brand"
              options={brandOptions}
              selectedOption={brandOptions.find((o) => o.value === sourceBrand)}
              onChange={handleBrandChange}
              loading={brandLoading}
              size="md"
            />
          )}

          <SingleSelect
            label="Source cube"
            placeholder={getSourceCubePlaceholder()}
            options={sourceCubeOptions}
            selectedOption={sourceCubeOptions.find(
              (o) => o.value === sourceCubeId
            )}
            onChange={(val) => {
              setSourceCubeId(val || undefined);
              setYearSource(undefined);
            }}
            loading={budgetSource && budgetVersionsLoading}
            disabled={needsSourceBrand && !sourceBrand}
            size="md"
          />

          <SingleSelect
            label="Source year"
            options={sourceYearOptions}
            selectedOption={sourceYearOptions.find(
              (o) => o.value === yearSource
            )}
            onChange={(val) => setYearSource(val || undefined)}
            size="md"
          />

          <div className={copyRowTargetField}>
            <SingleSelect
              label="Target year"
              options={targetYearOptions}
              selectedOption={targetYearOptions.find(
                (o) => o.value === yearTarget
              )}
              onChange={(val) => setYearTarget(val || undefined)}
              size="md"
            />
          </div>
        </div>

        <p className={hintText}>
          Optionally filter which part of the source data to copy.
        </p>
        <CubeContextFilterGrid
          options={cubeContext?.options}
          filteredOptions={filteredContext?.options}
          selections={contextSelections}
          hasActiveFilters={!!activeFilters}
          isLoading={contextLoading}
          setSelected={handleContextChange}
        />

        {yearSource &&
          yearTarget &&
          yearSource === yearTarget &&
          isSameCubeAsTarget && (
            <p className={errorText}>
              Source and target year must differ when copying within the same
              cube.
            </p>
          )}

        <div className={buttonRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDialogOpen(true)}
            disabled={!isValid}
            title={getDisabledReason()}
          >
            Copy
          </Button>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Copy Year"
        confirmText="Copy"
        onConfirm={handleConfirm}
        isLoading={copyCubeYear.isPending}
      >
        <p>
          Copy data from{" "}
          <strong>
            {producerLabel} · {versionLabel} · {yearSource}
          </strong>{" "}
          to year <strong>{yearTarget}</strong>. Existing data in the target
          year will be overwritten. This operation is irreversible.
        </p>
        {Object.entries(contextSelections).some(
          ([, vals]) => vals.length > 0
        ) && (
          <>
            <p>
              <strong>Context filter:</strong>
            </p>
            <ul>
              {Object.entries(contextSelections)
                .filter(([, vals]) => vals.length > 0)
                .map(([key, vals]) => (
                  <li key={key}>
                    <strong>{getFieldLabel(key)}:</strong> {vals.join(", ")}
                  </li>
                ))}
            </ul>
          </>
        )}
      </ConfirmOperationDialog>
    </>
  );
}
